// src/app/services/connectivity.service.ts
import { Injectable, NgZone } from "@angular/core"
import { App } from "@capacitor/app"
import { Network, ConnectionStatus } from "@capacitor/network"
import { HttpClient } from "@angular/common/http"
import { BehaviorSubject, Observable, Subscription, firstValueFrom, interval, of } from "rxjs"
import { catchError, map, shareReplay, startWith } from "rxjs/operators"

type ConnectivityState = {
  networkConnected: boolean   // device says it has a network path
  internetReachable: boolean  // we confirmed with a ping
  connectionType?: string
}

@Injectable({ providedIn: "root" })
export class ConnectivityService {
  private readonly state$ = new BehaviorSubject<ConnectivityState>({
    networkConnected: true,
    internetReachable: true
  })
  private isAppActive = true
  private monitorSub?: Subscription

  // Change this to something you control (best): e.g. https://api.yourdomain.com/health
  private readonly pingUrl = "https://api.aitempo.online/health"

  constructor(
    private http: HttpClient,
    private zone: NgZone
  ) {}

  async init() {
    await this.refreshConnectivity()

    Network.addListener("networkStatusChange", (status) => {
      if (!this.isAppActive) return
      void this.updateStateFromStatus(status)
    })

    App.addListener("appStateChange", ({ isActive }) => {
      this.isAppActive = isActive

      if (isActive) {
        void this.refreshConnectivity()
      }
    })
  }

  /**
   * Call this when you need a "real internet" answer.
   * Uses a very small request; replace with your own /health endpoint for best reliability.
   */
  checkInternetOnce(timeoutMs = 4000): Observable<boolean> {
    // HEAD is ideal but some endpoints reject it; GET generate_204 is common.
    return this.http.get(this.pingUrl, { observe: "response", responseType: "text" as "json" }).pipe(
      map(res => res.status >= 200 && res.status < 400),
      catchError(() => of(false))
    )
  }

  /**
   * Background monitoring (optional): pings periodically only if networkConnected is true.
   */
  startInternetMonitor(everyMs = 15000) {
    this.monitorSub?.unsubscribe()

    this.monitorSub = interval(everyMs)
      .pipe(
        startWith(0)
      )
      .subscribe(() => {
        if (!this.isAppActive) return
        void this.refreshConnectivity()
      })
  }

  get connectivity$(): Observable<ConnectivityState> {
    return this.state$.asObservable().pipe(shareReplay(1))
  }

  get isOnline$(): Observable<boolean> {
    return this.connectivity$.pipe(map(s => s.networkConnected && s.internetReachable))
  }

  private async refreshConnectivity() {
    const status = await Network.getStatus()
    await this.updateStateFromStatus(status)
  }

  private async updateStateFromStatus(status: ConnectionStatus) {
    const internetReachable = status.connected
      ? await firstValueFrom(this.checkInternetOnce())
      : false

    this.zone.run(() => {
      this.state$.next({
        networkConnected: status.connected,
        internetReachable,
        connectionType: status.connectionType
      })
    })
  }
}
